/**
 * Firestore helpers for newsletter subscriptions.
 * Collection: artifacts/{appId}/public/data/newsletterSubscriptions
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type QuerySnapshot,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db, appId } from './firebase';
import {
  sendBrevoEmail,
  DEFAULT_BREVO_SENDER_NAME,
  DEFAULT_BREVO_SENDER_EMAIL,
} from './utils/brevo';

export type NewsletterSubscriptionStatus = 'active' | 'unsubscribed';

export interface NewsletterSubscription {
  id: string;
  email: string;
  firstName?: string | null;
  status: NewsletterSubscriptionStatus;
  /** Source identifier, e.g. "footer_newsletter", "inline_newsletter" */
  source: string;
  subscribedAt: Timestamp | Date;
  unsubscribedAt?: Timestamp | Date | null;
  /** Whether the subscriber was successfully synced to Brevo contact list */
  brevoSynced?: boolean;
  /** When the welcome email was sent (admin or sync); null if not sent yet */
  welcomeEmailSentAt?: Timestamp | Date | null;
}

const newsletterSubscriptionsRef = () =>
  collection(db, 'artifacts', appId, 'public', 'data', 'newsletterSubscriptions');

function toNewsletterSubscription(id: string, data: DocumentData): NewsletterSubscription {
  return {
    id,
    email: data.email ?? '',
    firstName: data.firstName ?? null,
    status: (data.status as NewsletterSubscriptionStatus) ?? 'active',
    source: data.source ?? 'unknown',
    subscribedAt: data.subscribedAt ?? new Date(),
    unsubscribedAt: data.unsubscribedAt ?? null,
    brevoSynced: data.brevoSynced ?? false,
    welcomeEmailSentAt: data.welcomeEmailSentAt ?? null,
  };
}

/**
 * Subscribe an email to the newsletter.
 * Returns 'created' when a new subscription is saved, or 'already_subscribed'
 * when the email already exists with status 'active'.
 */
export async function subscribeToNewsletter(
  email: string,
  firstName?: string | null,
  source = 'unknown'
): Promise<'created' | 'already_subscribed'> {
  const normalizedEmail = email.trim().toLowerCase();

  // Check for existing active subscription with this email
  const existing = await getDocs(
    query(newsletterSubscriptionsRef(), where('email', '==', normalizedEmail), where('status', '==', 'active'))
  );

  if (!existing.empty) {
    return 'already_subscribed';
  }

  await addDoc(newsletterSubscriptionsRef(), {
    email: normalizedEmail,
    firstName: firstName?.trim() || null,
    status: 'active',
    source,
    subscribedAt: serverTimestamp(),
    unsubscribedAt: null,
    brevoSynced: false,
  });

  return 'created';
}

/**
 * Mark a subscription as unsubscribed (soft delete — preserves audit record).
 */
export async function unsubscribeFromNewsletter(id: string): Promise<void> {
  await updateDoc(doc(newsletterSubscriptionsRef(), id), {
    status: 'unsubscribed',
    unsubscribedAt: serverTimestamp(),
  });
}

/**
 * Mark a subscription's Brevo sync status.
 */
export async function markBrevoSynced(id: string, synced: boolean): Promise<void> {
  await updateDoc(doc(newsletterSubscriptionsRef(), id), { brevoSynced: synced });
}

/**
 * Mark that the welcome email was sent for this subscription (called after successful send).
 */
export async function markWelcomeEmailSent(id: string): Promise<void> {
  await updateDoc(doc(newsletterSubscriptionsRef(), id), {
    welcomeEmailSentAt: serverTimestamp(),
  });
}

/**
 * Hard delete a subscription record.
 */
export async function deleteNewsletterSubscription(id: string): Promise<void> {
  await deleteDoc(doc(newsletterSubscriptionsRef(), id));
}

/**
 * Subscribe to real-time newsletter subscription updates (admin use).
 * Returns an Unsubscribe function to stop listening.
 */
export function onNewsletterSubscriptions(
  callback: (subscriptions: NewsletterSubscription[]) => void
): Unsubscribe {
  const q = query(newsletterSubscriptionsRef(), orderBy('subscribedAt', 'desc'));
  return onSnapshot(q, (snap: QuerySnapshot) => {
    callback(snap.docs.map((d) => toNewsletterSubscription(d.id, d.data())));
  });
}

/** Subject and HTML for the newsletter welcome email (SMBPrime context). */
export function buildNewsletterWelcomeEmail(
  firstName?: string | null
): { subject: string; htmlContent: string } {
  const subject = "Welcome to SMBPrime | Government contracting insights";
  const greeting = firstName?.trim()
    ? `Hi ${firstName.trim()},`
    : "Hi there,";
  const htmlContent = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:16px;line-height:1.5;color:#1f2937;">
  <div style="max-width:560px;margin:0 auto;padding:24px;">
    <p style="margin:0 0 16px;">${greeting}</p>
    <p style="margin:0 0 16px;">Thanks for signing up for the SMBPrime newsletter. You're on the list.</p>
    <p style="margin:0 0 16px;">We help small businesses navigate federal subcontracting and connect with the right prime contractors — so you can win more work without hiring a full business development team. You'll get insights, tips, and updates on government contracting delivered to your inbox.</p>
    <p style="margin:0 0 24px;">Ready to take the next step? <a href="https://smbprime.ai" style="color:#2563eb;text-decoration:underline;">Book a Strategy Call</a> or explore our site to see how we book qualified meetings with primes so you can focus on delivering great work.</p>
    <p style="margin:0;color:#6b7280;font-size:14px;">— The SMBPrime team</p>
  </div>
</body>
</html>`.trim();
  return { subject, htmlContent };
}

/**
 * Send the newsletter welcome email via Brevo (admin/sync context only — API key must not be exposed to client).
 * No-ops if apiKey or toEmail is empty. Logs and does not throw so sync flow can continue.
 */
export async function sendNewsletterWelcomeEmail(
  apiKey: string,
  toEmail: string,
  firstName?: string | null
): Promise<void> {
  const email = toEmail?.trim().toLowerCase();
  if (!apiKey?.trim() || !email) return;

  const { subject, htmlContent } = buildNewsletterWelcomeEmail(firstName);
  try {
    await sendBrevoEmail({
      apiKey: apiKey.trim(),
      toEmails: [email],
      senderName: DEFAULT_BREVO_SENDER_NAME,
      senderEmail: DEFAULT_BREVO_SENDER_EMAIL,
      subject,
      htmlContent,
    });
  } catch (err) {
    console.error(
      `[newsletter] Welcome email failed for ${email}:`,
      err instanceof Error ? err.message : err
    );
    throw err;
  }
}
